import { useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTour } from './usePageTour';

interface SuiteTourStep {
  element: string;
  popover: {
    title: string;
    description: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
  };
  action?: () => void;
  waitForNavigation?: boolean;
  expectedPath?: string;
}

export const useSuiteTour = () => {
  const { startTour, cleanup, isActive, driverRef } = usePageTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSuite, setCurrentSuite] = useState<string | null>(null);
  const stepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  const currentStepIndexRef = useRef(0);

  // Function to start the complete tour with all steps
  const startCompleteTour = useCallback((steps: SuiteTourStep[]) => {
    // Convert our steps to Driver.js format (without onNext callbacks)
    const driverSteps = steps.map((step) => ({
      element: step.element,
      popover: {
        title: step.popover.title,
        description: step.popover.description,
        side: (step.popover.side || 'right') as 'left' | 'right' | 'top' | 'bottom',
        align: 'start' as const
      }
    }));

    // Store steps for reference
    currentStepIndexRef.current = 0;

    // Function to handle step actions
    const handleStepAction = (stepIndex: number) => {
      const currentStep = steps[stepIndex];
      if (currentStep) {
        // Execute action if present
        if (currentStep.action) {
          currentStep.action();
          
          // If this step requires waiting for navigation, wait for it
          if (currentStep.waitForNavigation && currentStep.expectedPath) {
            isNavigatingRef.current = true;
            // Wait for navigation with timeout
            let navigationCheckCount = 0;
            const maxNavigationChecks = 20; // 10 seconds max wait
            
            const checkNavigation = () => {
              navigationCheckCount++;
              const currentPath = window.location.pathname;
              
              // Check if navigation is complete (either path change or tab switch)
              const isNavigationComplete = currentPath.includes(currentStep.expectedPath!) || 
                (currentStep.expectedPath?.includes('/units') && document.querySelector('.ant-tabs-tab[data-node-key="units"].ant-tabs-tab-active')) ||
                (currentStep.expectedPath?.includes('/parkings') && document.querySelector('.ant-tabs-tab[data-node-key="parkings"].ant-tabs-tab-active')) ||
                (currentStep.expectedPath?.includes('/amenities') && document.querySelector('.ant-tabs-tab[data-node-key="amenities"].ant-tabs-tab-active'));
              
              if (isNavigationComplete) {
                isNavigatingRef.current = false;
                
                // Clean up current tour and restart from the next step
                stepTimeoutRef.current = setTimeout(() => {
                  try {
                    // Clean up current tour
                    if (driverRef.current) {
                      driverRef.current.destroy();
                    }
                  } catch (error) {
                    // Silently handle errors
                  }
                  
                  // Wait a bit for cleanup, then restart from next step
                  setTimeout(() => {
                    const remainingSteps = steps.slice(currentStepIndexRef.current);
                    
                    if (remainingSteps.length > 0) {
                      // Convert remaining steps to Driver.js format
                      const driverSteps = remainingSteps.map((step) => ({
                        element: step.element,
                        popover: {
                          title: step.popover.title,
                          description: step.popover.description,
                          side: (step.popover.side || 'right') as 'left' | 'right' | 'top' | 'bottom',
                          align: 'start' as const
                        }
                      }));
                      
                      // Start new tour with remaining steps
                      startTour(driverSteps, {
                        onDestroyed: () => {
                          document.removeEventListener('click', handleNextClick, true);
                          observer.disconnect();
                          if (stepTimeoutRef.current) {
                            clearTimeout(stepTimeoutRef.current);
                          }
                          setCurrentSuite(null);
                          // Reset step index
                          driverRef.current = null;
                        }
                      });
                      
                      // Re-add event listeners for the new tour (remove existing first)
                      document.removeEventListener('click', handleNextClick, true);
                      document.addEventListener('click', handleNextClick, true);
                    }
                  }, 1000); // Increased to 1000ms to ensure tab content is loaded
                }, 1000); // Reduced from 2000ms to 1000ms
              } else if (navigationCheckCount >= maxNavigationChecks) {
                isNavigatingRef.current = false;
                
                // Clean up and restart from next step
                stepTimeoutRef.current = setTimeout(() => {
                  try {
                    // Clean up current tour
                    if (driverRef.current) {
                      driverRef.current.destroy();
                    }
                  } catch (error) {
                    // Silently handle errors
                  }
                  
                  // Wait a bit for cleanup, then restart from next step
                  setTimeout(() => {
                    const remainingSteps = steps.slice(currentStepIndexRef.current);
                    
                    if (remainingSteps.length > 0) {
                      // Convert remaining steps to Driver.js format
                      const driverSteps = remainingSteps.map((step) => ({
                        element: step.element,
                        popover: {
                          title: step.popover.title,
                          description: step.popover.description,
                          side: (step.popover.side || 'right') as 'left' | 'right' | 'top' | 'bottom',
                          align: 'start' as const
                        }
                      }));
                      
                      // Start new tour with remaining steps
                      startTour(driverSteps, {
                        onDestroyed: () => {
                          document.removeEventListener('click', handleNextClick, true);
                          observer.disconnect();
                          if (stepTimeoutRef.current) {
                            clearTimeout(stepTimeoutRef.current);
                          }
                          setCurrentSuite(null);
                          // Reset step index
                          driverRef.current = null;
                        }
                      });
                      
                      // Re-add event listeners for the new tour (remove existing first)
                      document.removeEventListener('click', handleNextClick, true);
                      document.addEventListener('click', handleNextClick, true);
                    }
                  }, 1000); // Increased to 1000ms to ensure tab content is loaded
                }, 1000); // Reduced from 1000ms to 1000ms
              } else {
                // Keep checking
                stepTimeoutRef.current = setTimeout(checkNavigation, 500);
              }
            };
            checkNavigation();
          }
        }
      }
    };

    // Add event listener for Next button clicks with more specific targeting
    const handleNextClick = (event: Event) => {
      const target = event.target as HTMLElement;
      
      // Check if it's a Driver.js Next button
      const isDriverNextButton = target && (
        target.textContent?.includes('Next') ||
        target.className?.includes('driver-next') ||
        target.parentElement?.className?.includes('driver-next') ||
        target.closest('.driver-popover')?.querySelector('[data-driver-next]') === target
      );
      
      if (isDriverNextButton) {
        handleStepAction(currentStepIndexRef.current);
        
        // Update current step index
        currentStepIndexRef.current += 1;
        // Update step index
      }
    };

    // Add event listener with capture to catch events earlier
    document.addEventListener('click', handleNextClick, true);

    // Add MutationObserver to detect Driver.js elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if it's a Driver.js popover
              if (element.classList?.contains('driver-popover') || element.querySelector?.('.driver-popover')) {
                // Find the Next button and add direct event listener
                const nextButton = element.querySelector('[data-driver-next]') || 
                                 element.querySelector('button[class*="next"]') ||
                                 Array.from(element.querySelectorAll('button')).find(btn => 
                                   btn.textContent?.includes('Next')
                                 );
                
                if (nextButton) {
                  nextButton.addEventListener('click', (e) => {
                    try {
                      e.stopPropagation();
                      handleStepAction(currentStepIndexRef.current);
                      currentStepIndexRef.current += 1;
                      // Update step index
                    } catch (error) {
                      // Silently handle errors
                    }
                  });
                }
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Start the complete tour
    startTour(driverSteps, {
      onDestroyed: () => {
        document.removeEventListener('click', handleNextClick, true);
        observer.disconnect();
        if (stepTimeoutRef.current) {
          clearTimeout(stepTimeoutRef.current);
        }
        setCurrentSuite(null);
        // Reset step index
        driverRef.current = null;
      }
    });
  }, [startTour, location.pathname, driverRef]);

  const startSuiteTour = useCallback(async (suiteId: string) => {
    try {
      setCurrentSuite(suiteId);
      // Reset step index
      cleanup();

      // Wait for cleanup
      await new Promise(resolve => setTimeout(resolve, 300));

      if (suiteId === 'resource-management') {
        // Check if mobile
        const isMobile = window.innerWidth < 768;
        
        const steps = isMobile ? [
          // Mobile-specific tour steps
          // Step 1: Start from Help page
          {
            element: '.suite-tour-item.resource-management',
            popover: {
              title: 'Resource Management Suite',
              description: 'Welcome to the Resource Management Suite tour! This will guide you through all the resource management features.\n\n💡 Click "Next" to begin.',
              side: 'bottom' as const
            }
          },
          // Step 2: Mobile - Expand navigation drawer
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'First, let\'s open the navigation drawer to access the menu.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 3: Mobile - Navigate to Properties
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-home)',
            popover: {
              title: 'Properties Management',
              description: 'Now let\'s navigate to the Properties management section.\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const propertiesMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-home)') as HTMLElement;
              if (propertiesMenuItem) {
                propertiesMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/blocks'
          },
          // Step 4: Mobile - Property Categories tabs
          {
            element: '.properties-tabs .ant-tabs-nav',
            popover: {
              title: 'Property Categories',
              description: 'Here you can switch between different property categories: Blocks, Units, Parking, and Amenities.',
              side: 'bottom' as const
            }
          },
          // Step 5: Mobile - Blocks List (not table)
          {
            element: '.ant-list',
            popover: {
              title: 'Blocks Management',
              description: 'This is the Blocks management list where you can view, add, edit, and manage building blocks.',
              side: 'bottom' as const
            }
          },
          // Step 6: Mobile - Switch to Units tab
          {
            element: '.ant-tabs-tab[data-node-key="units"]',
            popover: {
              title: 'Apartment Units',
              description: 'Switching to the Units tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const unitsTab = document.querySelector('.ant-tabs-tab[data-node-key="units"]') as HTMLElement;
              if (unitsTab) {
                unitsTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/units'
          },
          // Step 7: Mobile - Units List
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .ant-list',
            popover: {
              title: 'Units Management',
              description: 'This is the Units management list where you can view, add, edit, and manage apartment units.',
              side: 'bottom' as const
            }
          },
          // Step 8: Mobile - Switch to Parking tab
          {
            element: '.ant-tabs-tab[data-node-key="parkings"]',
            popover: {
              title: 'Parking Spaces',
              description: 'Switching to the Parking tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const parkingTab = document.querySelector('.ant-tabs-tab[data-node-key="parkings"]') as HTMLElement;
              if (parkingTab) {
                parkingTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/parkings'
          },
          // Step 9: Mobile - Parking List
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .ant-list',
            popover: {
              title: 'Parking Management',
              description: 'This is the Parking management list where you can view, add, edit, and manage parking spaces.',
              side: 'bottom' as const
            }
          },
          // Step 10: Mobile - Switch to Amenities tab
          {
            element: '.ant-tabs-tab[data-node-key="amenities"]',
            popover: {
              title: 'Amenities',
              description: 'Switching to the Amenities tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const amenitiesTab = document.querySelector('.ant-tabs-tab[data-node-key="amenities"]') as HTMLElement;
              if (amenitiesTab) {
                amenitiesTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/amenities'
          },
          // Step 11: Mobile - Amenities List
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .ant-list',
            popover: {
              title: 'Amenities Management',
              description: 'This is the Amenities management list where you can view, add, edit, and manage amenities.',
              side: 'bottom' as const
            }
          },
          // Step 12: Mobile - Open drawer for Residents navigation
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'Let\'s open the navigation drawer to access the Residents menu.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 13: Mobile - Navigate to Residents
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-team)',
            popover: {
              title: 'Residents Management',
              description: 'Now let\'s navigate to the Residents management section.\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const residentsMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-team)') as HTMLElement;
              if (residentsMenuItem) {
                residentsMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/residents'
          },
          // Step 14: Mobile - Residents List
          {
            element: '.ant-list',
            popover: {
              title: 'Residents Management',
              description: 'This is the Residents management list where you can view, add, edit, and manage residents.',
              side: 'bottom' as const
            }
          },
          // Step 15: Mobile - Open drawer for Employees navigation
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'Let\'s open the navigation drawer to access the Employees menu.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 16: Mobile - Navigate to Employees
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-user)',
            popover: {
              title: 'Employees Management',
              description: 'Now let\'s navigate to the Employees management section.\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const employeesMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-user)') as HTMLElement;
              if (employeesMenuItem) {
                employeesMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/employees'
          },
          // Step 17: Mobile - Employees List
          {
            element: '.ant-list',
            popover: {
              title: 'Employees Management',
              description: 'This is the Employees management list where you can view, add, edit, and manage employees.',
              side: 'bottom' as const
            }
          },
          // Step 18: Mobile - Open drawer for Vendors navigation
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'Let\'s open the navigation drawer to access the Vendors menu.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 19: Mobile - Navigate to Vendors
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-appstore-add)',
            popover: {
              title: 'Vendors Management',
              description: 'Now let\'s navigate to the Vendors management section.\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const vendorsMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-appstore-add)') as HTMLElement;
              if (vendorsMenuItem) {
                vendorsMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/vendors'
          },
          // Step 20: Mobile - Vendors List
          {
            element: '.ant-list',
            popover: {
              title: 'Vendors Management',
              description: 'This is the Vendors management list where you can view, add, edit, and manage vendors.',
              side: 'bottom' as const
            }
          },
          // Step 21: Mobile - Open drawer for Assets navigation
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'Let\'s open the navigation drawer to access the Assets menu.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 22: Mobile - Navigate to Assets
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-tool)',
            popover: {
              title: 'Assets Management',
              description: 'Now let\'s navigate to the Assets management section.\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const assetsMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-tool)') as HTMLElement;
              if (assetsMenuItem) {
                assetsMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/assets'
          },
          // Step 23: Mobile - Assets List
          {
            element: '.ant-list',
            popover: {
              title: 'Assets Management',
              description: 'This is the Assets management list where you can view, add, edit, and manage assets.',
              side: 'bottom' as const
            }
          },
          // Step 24: Mobile - Open drawer for Help navigation
          {
            element: '.ant-layout-header .ant-btn:has(.anticon-menu)',
            popover: {
              title: 'Open Navigation',
              description: 'Let\'s open the navigation drawer to return to the Help page.\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const menuButton = document.querySelector('.ant-layout-header .ant-btn:has(.anticon-menu)') as HTMLElement;
              if (menuButton) {
                menuButton.click();
              }
            }
          },
          // Step 25: Mobile - Return to Help
          {
            element: '.ant-drawer .ant-menu-item:has(.anticon-question-circle)',
            popover: {
              title: 'Return to Help',
              description: 'Returning to the Help page...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              const helpMenuItem = document.querySelector('.ant-drawer .ant-menu-item:has(.anticon-question-circle)') as HTMLElement;
              if (helpMenuItem) {
                helpMenuItem.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/help'
          },
          // Step 26: Mobile - Complete tour
          {
            element: '.suite-tour-item.resource-management',
            popover: {
              title: 'Tour Complete!',
              description: 'Congratulations! You\'ve completed the Resource Management Suite tour. You now know how to navigate and manage all your resources.\n\n🎉 Thank you for taking the tour!',
              side: 'bottom' as const
            }
          }
        ] : [
          // Desktop tour steps
          // Step 1: Start from Help page
          {
            element: '.suite-tour-item.resource-management',
            popover: {
              title: 'Resource Management Suite',
              description: 'Welcome to the Resource Management Suite tour! This will guide you through all the resource management features.\n\n💡 Click "Next" to begin.',
              side: 'bottom' as const
            }
          },
          // Step 2: Desktop - Navigate to Properties
          {
            element: '.ant-menu-item:has(.anticon-home)',
            popover: {
              title: 'Properties Management',
              description: 'Navigating to the Properties management section...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/resources/properties');
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/blocks'
          },
          // Step 3: Desktop - Property Categories tabs
          {
            element: '.properties-tabs .ant-tabs-nav',
            popover: {
              title: 'Property Categories',
              description: 'Here you can switch between different property categories: Blocks, Units, Parking, and Amenities.',
              side: 'bottom' as const
            }
          },
          // Step 4: Desktop - Blocks Table
          {
            element: '.data-table',
            popover: {
              title: 'Blocks Management',
              description: 'This is the Blocks management table where you can view, add, edit, and manage building blocks.',
              side: 'bottom' as const
            }
          },
          // Step 5: Desktop - Switch to Units tab
          {
            element: '.ant-tabs-tab[data-node-key="units"]',
            popover: {
              title: 'Apartment Units',
              description: 'Switching to the Units tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const unitsTab = document.querySelector('.ant-tabs-tab[data-node-key="units"]') as HTMLElement;
              if (unitsTab) {
                unitsTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/units'
          },
          // Step 6: Desktop - Units Table
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .data-table-card',
            popover: {
              title: 'Units Management',
              description: 'This is the Units management table where you can view, add, edit, and manage apartment units.',
              side: 'bottom' as const
            }
          },
          // Step 7: Desktop - Switch to Parking tab
          {
            element: '.ant-tabs-tab[data-node-key="parkings"]',
            popover: {
              title: 'Parking Spaces',
              description: 'Switching to the Parking tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const parkingTab = document.querySelector('.ant-tabs-tab[data-node-key="parkings"]') as HTMLElement;
              if (parkingTab) {
                parkingTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/parkings'
          },
          // Step 8: Desktop - Parking Table
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .data-table-card',
            popover: {
              title: 'Parking Management',
              description: 'This is the Parking management table where you can view, add, edit, and manage parking spaces.',
              side: 'bottom' as const
            }
          },
          // Step 9: Desktop - Switch to Amenities tab
          {
            element: '.ant-tabs-tab[data-node-key="amenities"]',
            popover: {
              title: 'Amenities',
              description: 'Switching to the Amenities tab...\n\n💡 Click "Next" to continue.',
              side: 'bottom' as const
            },
            action: () => {
              const amenitiesTab = document.querySelector('.ant-tabs-tab[data-node-key="amenities"]') as HTMLElement;
              if (amenitiesTab) {
                amenitiesTab.click();
              }
            },
            waitForNavigation: true,
            expectedPath: '/resources/properties/amenities'
          },
          // Step 10: Desktop - Amenities Table
          {
            element: '.ant-tabs-content-holder .ant-tabs-tabpane-active .data-table-card',
            popover: {
              title: 'Amenities Management',
              description: 'This is the Amenities management table where you can view, add, edit, and manage amenities.',
              side: 'bottom' as const
            }
          },
          // Step 11: Desktop - Navigate to Residents
          {
            element: '.ant-menu-item:has(.anticon-team)',
            popover: {
              title: 'Residents Management',
              description: 'Navigating to the Residents management section...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/resources/residents');
            },
            waitForNavigation: true,
            expectedPath: '/resources/residents'
          },
          // Step 12: Desktop - Residents Table
          {
            element: '.data-table-card',
            popover: {
              title: 'Residents Management',
              description: 'This is the Residents management table where you can view, add, edit, and manage residents.',
              side: 'bottom' as const
            }
          },
          // Step 13: Desktop - Navigate to Employees
          {
            element: '.ant-menu-item:has(.anticon-user)',
            popover: {
              title: 'Employees Management',
              description: 'Navigating to the Employees management section...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/resources/employees');
            },
            waitForNavigation: true,
            expectedPath: '/resources/employees'
          },
          // Step 14: Desktop - Employees Table
          {
            element: '.data-table-card',
            popover: {
              title: 'Employees Management',
              description: 'This is the Employees management table where you can view, add, edit, and manage employees.',
              side: 'bottom' as const
            }
          },
          // Step 15: Desktop - Navigate to Vendors
          {
            element: '.ant-menu-item:has(.anticon-appstore-add)',
            popover: {
              title: 'Vendors Management',
              description: 'Navigating to the Vendors management section...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/resources/vendors');
            },
            waitForNavigation: true,
            expectedPath: '/resources/vendors'
          },
          // Step 16: Desktop - Vendors Table
          {
            element: '.data-table-card',
            popover: {
              title: 'Vendors Management',
              description: 'This is the Vendors management table where you can view, add, edit, and manage vendors.',
              side: 'bottom' as const
            }
          },
          // Step 17: Desktop - Navigate to Assets
          {
            element: '.ant-menu-item:has(.anticon-tool)',
            popover: {
              title: 'Assets Management',
              description: 'Navigating to the Assets management section...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/resources/assets');
            },
            waitForNavigation: true,
            expectedPath: '/resources/assets'
          },
          // Step 18: Desktop - Assets Table
          {
            element: '.data-table-card',
            popover: {
              title: 'Assets Management',
              description: 'This is the Assets management table where you can view, add, edit, and manage assets.',
              side: 'bottom' as const
            }
          },
          // Step 19: Desktop - Return to Help
          {
            element: '.ant-menu-item:has(.anticon-question-circle)',
            popover: {
              title: 'Return to Help',
              description: 'Returning to the Help page...\n\n💡 Click "Next" to continue.',
              side: 'right' as const
            },
            action: () => {
              navigate('/help');
            },
            waitForNavigation: true,
            expectedPath: '/help'
          },
          // Step 20: Desktop - Complete tour
          {
            element: '.suite-tour-item.resource-management',
            popover: {
              title: 'Tour Complete!',
              description: 'Congratulations! You\'ve completed the Resource Management Suite tour. You now know how to navigate and manage all your resources.\n\n🎉 Thank you for taking the tour!',
              side: 'bottom' as const
            }
          }
        ];

        // Store steps for reference

        // Start the complete tour
        startCompleteTour(steps);
      } else {
        // Other suite tours can be added here
      }
      
    } catch (error) {
      setCurrentSuite(null);
      // Reset step index
    }
  }, [startTour, cleanup, navigate, startCompleteTour]);

  // Cleanup timeout on unmount
  const cleanupRef = useCallback(() => {
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
    }
    cleanup();
  }, [cleanup]);

  return {
    startSuiteTour,
    stopTour: cleanupRef,
    cleanup: cleanupRef,
    isActive: isActive || currentSuite !== null,
    currentSuite
  };
};
